/** Words for the auth screens. No em dashes anywhere. */
import type { ApiTokenInfo, Capability, CapabilityTier, InviteInfo, LinkedIdentity, Me, Member, ProviderStatus, Role, SignInProviderId } from '@orgus/protocol';
import { AI_CAPABILITIES, MICROSOFT_CONSUMERS_TENANT, ROLES, ROLE_LABEL, ROLE_RANK, SIGN_IN_PROVIDER_LABEL, capabilityByName, isSignInProvider, tierRank, tokenCeiling } from '@orgus/protocol';
import type { UiError } from '../../lib/api.ts';
import type { PillStatus, Tone } from '@orgus/ui';

/** 'GitHub', 'Google', 'Microsoft'; anything else is shown as given. */
export function providerLabel(id: string | null | undefined): string {
  return isSignInProvider(id) ? SIGN_IN_PROVIDER_LABEL[id] : (id ?? '');
}

/**
 * What a refused sign-in means, for the banner on the sign-in screen. `login` is the
 * validated value from the hub's redirect (an email the provider proved, or a GitHub login);
 * with no `provider` the words fit any provider. Only `login` and the provider label are
 * ever interpolated. `hosted`: the server is the hosted control plane, where there is no hub
 * machine, terminal or auth.json to point at.
 */
export function signinErrorMessage(code: string, login: string | null, provider?: SignInProviderId | null, hosted = false): UiError {
  const p = provider ? SIGN_IN_PROVIDER_LABEL[provider] : null;
  const here = hosted ? 'this workspace' : 'this hub';
  const Here = hosted ? 'This workspace' : 'This hub';
  const Server = hosted ? 'Orgus' : 'The hub';
  const them = p ?? 'the sign-in provider';
  const Them = p ?? 'The sign-in provider';
  // A refused account gets a short title; the screen names the account itself on its own line.
  switch (code) {
    case 'not-allowed':
      return provider === 'github'
        ? {
            what: 'Not a member yet.',
            why: 'No allow rule names this account or one of its organisations.',
            next: `Ask an admin to add your GitHub account to ${here}, then sign in again.`,
          }
        : {
            what: 'Not a member yet.',
            why: p
              ? 'No invite, linked account or join rule lets this account in. An invite only works through the link that was sent. A sign-in an admin added for you stops working if that admin leaves before you confirm it.'
              : 'No allow rule, invite, linked account or join rule lets this account in.',
            next: `Open the invite link you were sent, or ask an admin of ${here} to invite you.`,
          };
    case 'not-configured':
      return {
        what: p ? `Sign in with ${p} is not set up on ${here}.` : `That sign-in method is not set up on ${here}.`,
        why: p ? `${Server} has no ${p} client id and secret, or they have a problem.` : `${Server} has no client id and secret for it, or they have a problem.`,
        next: hosted ? 'Use another way to sign in, or ask the owner to finish setting it up.' : 'Use another way to sign in or the owner link, then set it up in Settings, Sign-in.',
      };
    case 'state':
      return {
        what: 'The sign-in did not finish.',
        why: 'It took over 10 minutes, or started in another tab.',
        next: p ? `Start again with Sign in with ${p}.` : 'Start the sign-in again.',
      };
    case 'busy':
      return {
        what: 'Too many sign-ins are in progress from this network.',
        why: hosted
          ? 'Orgus limits unfinished and failed sign-ins per network address.'
          : 'The hub limits unfinished and failed sign-ins per network address. Everyone behind the same proxy shares that limit unless the owner lists the proxy as trusted.',
        next: hosted ? 'Wait a minute and try again.' : 'Wait a minute and try again. If it keeps happening behind a proxy, ask the owner to add it to trustedProxies in auth.json.',
      };
    case 'denied':
      return { what: p ? `${p} sign-in was cancelled.` : 'The sign-in was cancelled.', why: `${Them} reported that access was not granted.`, next: 'Try again when you are ready.' };
    case 'bad-link':
      return {
        what: 'That sign-in link did not work.',
        why: hosted ? 'It is mistyped or cut short, or it was used already: each owner link works once.' : 'It is mistyped or cut short, or the hub has restarted since and made a new one.',
        next: hosted ? 'Sign in with your account, or ask the owner for a fresh link.' : 'Run orgus token on the hub machine and open the link it prints.',
      };
    case 'rate-limited':
      return { what: 'Too many sign-in attempts.', why: 'This address sent several sign-in links that did not work.', next: hosted ? 'Wait a minute, then open a fresh link.' : 'Wait a minute, then open the newest link from orgus token.' };
    case 'unreachable':
      return hosted
        ? { what: `Orgus could not reach ${them}.`, why: `${Them} did not answer in time.`, next: `Try again in a minute. If it keeps failing, ${them} may be having trouble.` }
        : { what: `The hub could not reach ${them}.`, why: 'The request from the hub failed or timed out.', next: "Check the hub machine's network, then try again." };
    case 'token-invalid':
      return {
        what: 'The sign-in was refused.',
        why: `The proof ${them} sent back did not pass ${hosted ? "Orgus's" : "the hub's"} checks. The audit log has the exact reason.`,
        next: hosted ? 'Try again. If it keeps failing, ask the owner to check the audit log.' : 'Try again. If it keeps failing, ask the owner to check the hub log and the clock on the hub machine.',
      };
    case 'email-unverified':
      return {
        what: `${Them} has not verified the email address of this account.`,
        why: `${Server} only accepts accounts whose address the provider has confirmed.`,
        next: `Verify the address with ${them}, or use another account.`,
      };
    case 'domain-not-allowed':
      return {
        what: 'Domain not allowed.',
        why: `${Here} only accepts Google accounts from certain Workspace domains. Personal Gmail accounts have no domain.`,
        next: 'Use your work Google account, or ask the owner to allow your domain in Settings, Sign-in.',
      };
    case 'tenant-not-allowed':
      return {
        what: 'Organisation not allowed.',
        why: `${Here} only accepts Microsoft accounts from certain organisations, and this account's organisation is not one of them.`,
        next: 'Use the Microsoft account of an allowed organisation, or ask the owner to allow yours in Settings, Sign-in.',
      };
    case 'already-member':
      return {
        what: `${login ?? 'This email address'} is already a member of ${here}.`,
        why: 'That member signs in another way. The same email from a second provider never joins an existing member on its own.',
        next: `Sign in the way you did before, then link ${p ?? 'this account'} in Settings, Sign-in.`,
      };
    case 'reverify':
      return {
        what: p ? `${p} sign-in is paused for your account.` : 'This sign-in is paused for your account.',
        why: `${Server} has not seen the account that admitted you recently, or the rule that let it in has changed.`,
        next: 'Sign in once the way you first joined, for example with GitHub or your work account. Then this account works again.',
      };
    case 'invite-invalid':
      return {
        what: 'This invite cannot be used.',
        why: 'It was used, revoked or has expired, or whoever made it can no longer give it.',
        next: 'Ask the person who invited you for a new link.',
      };
    case 'invite-mismatch':
      return {
        what: 'This account does not match the invite.',
        why: `The invite is for another email address, or it gives the admin or owner role and ${them} has not proved that this account owns the address.`,
        next: 'Sign in with the account for the address the invite was sent to. For an admin or owner invite, ask for a viewer or operator invite instead, then for the role.',
      };
    case 'link-conflict':
      return {
        what: p ? `This ${p} account is already linked to someone.` : 'This account is already linked to someone.',
        why: `One account can sign in as only one member of ${here}.`,
        next: 'Use another account, or ask an admin to unlink it from the other member first.',
      };
    case 'link-expired':
      return {
        what: p ? `Linking ${p} did not finish.` : 'Linking did not finish.',
        why: 'Your session ended first, or it took over 10 minutes.',
        next: 'Sign in again, then link it.',
      };
    case 'insecure':
      return {
        what: 'The hub did not sign you in.',
        why: 'This page reached the hub over plain HTTP from another computer, so the sign-in cookie would cross the network unencrypted.',
        next: 'Open the hub over HTTPS (for example Tailscale Serve), or on the hub computer itself.',
      };
    default:
      return {
        what: `${Them} did not complete the sign-in.`,
        why: hosted ? `${Them} refused the exchange. The Worker log has the details.` : `${Them} refused the exchange. The hub terminal has the details.`,
        next: hosted ? 'Try again. If it keeps failing, ask the owner to check the client id and secret.' : 'Try again. If it keeps failing, ask the owner to check the client id and secret in auth.json.',
      };
  }
}

/**
 * A role is a property, not a state: neutral badges, so it never reads as health beside a
 * status. Owner alone carries the accent (status.md, status colour only for status).
 */
export const ROLE_TONE: Record<Role, Tone> = { owner: 'accent', admin: 'neutral', operator: 'neutral', viewer: 'neutral' };

/** Roles someone with `mine` may hand out, highest first. */
export function grantableRoles(mine: Role): Role[] {
  return [...ROLES].filter((r) => ROLE_RANK[r] <= ROLE_RANK[mine]).reverse();
}

export const roleOptions = (roles: Role[]) => roles.map((r) => ({ value: r, label: ROLE_LABEL[r] }));

/** A tenant id short enough for a table cell. */
export function shortTenant(tid: string): string {
  if (tid === MICROSOFT_CONSUMERS_TENANT) return 'personal accounts';
  return tid.length > 13 ? `${tid.slice(0, 8)}...` : tid;
}

export function viaLabel(m: Member): string {
  if (m.provider === 'owner-link') return 'Owner link printed by the hub';
  if (!m.via || m.via === 'user') return m.provider === 'github' ? 'Named on the allow list' : 'Added by an admin';
  if (m.via.startsWith('org:')) return `Member of ${m.via.slice(4)}`;
  if (m.via === 'invite') return 'Accepted an invite';
  if (m.via === 'link') return 'Linked from the owner link';
  if (m.via.startsWith('domain:')) return `Joined from ${m.via.slice(7)}`;
  if (m.via.startsWith('tenant:')) return `Joined from Microsoft tenant ${shortTenant(m.via.slice(7))}`;
  return m.via;
}

/** The second line under a member's name: @login for GitHub, the email for Google and Microsoft. */
/** A role read as a value in a table cell: the same quiet text on every panel of Members. */
export const ROLE_VALUE_CLASS = 'text-ui text-fg-muted';

export function memberHandle(m: Member): string {
  if (m.provider === 'owner-link') return 'Bootstrap owner';
  if (m.provider === 'github') return `@${m.login}`;
  return m.email ?? (m.login.includes('@') ? m.login : `${providerLabel(m.provider)} account`);
}

export function signedInVia(me: Me): string {
  if (me.via === 'token') return me.token?.kind === 'agent' ? 'Using an agent token' : 'Using a personal token';
  if (me.via === 'owner-link') return 'Signed in with the owner link';
  return `Signed in with ${providerLabel(me.via)}`;
}

export function displayName(me: Me): string {
  return me.member?.name ?? me.member?.login ?? me.actor.id;
}

export const ACCOUNT_TYPE_LABEL: Record<NonNullable<LinkedIdentity['accountType']>, string> = {
  workspace: 'Google Workspace',
  personal: 'Personal account',
  work: 'Work or school account',
  guest: 'Guest in another organisation',
};

/** One line on how an identity was attached, seen by the member or an admin. */
export function addedLabel(i: LinkedIdentity, viewerIsMember: boolean): string {
  switch (i.addedVia) {
    case 'invite':
      return `Added by ${i.addedBy} through an invite`;
    case 'self':
      return viewerIsMember ? 'Linked by you' : 'Linked by the member';
    case 'owner-link':
      return 'Linked from the owner link';
    case 'join':
      return 'Joined with this account';
    default:
      return 'Signed up with this account';
  }
}

/** Status of a sign-in provider: icon plus label. */
export function providerStatusOf(p: Pick<ProviderStatus, 'configured' | 'needsAttention' | 'testIssuer'>): { status: PillStatus; label: string } {
  if (p.configured && p.needsAttention) return { status: 'needs-you', label: 'Needs attention' };
  if (p.configured && p.testIssuer) return { status: 'needs-you', label: 'Test identity provider' };
  if (p.configured) return { status: 'done', label: 'On' };
  if (p.needsAttention) return { status: 'failed', label: 'Misconfigured' };
  return { status: 'ended', label: 'Off' };
}

export function inviteStatusOf(i: Pick<InviteInfo, 'status' | 'effectiveRole' | 'revokedReason'>): { status: PillStatus; label: string } {
  switch (i.status) {
    case 'pending':
      return i.effectiveRole ? { status: 'queued', label: 'Pending' } : { status: 'failed', label: 'Dead: creator gone' };
    case 'used':
      return { status: 'done', label: 'Used' };
    case 'expired':
      return { status: 'ended', label: 'Expired' };
    default:
      return {
        status: 'ended',
        label: i.revokedReason === 'creator-removed' ? 'Revoked: creator removed' : i.revokedReason === 'member-removed' ? 'Revoked: member removed' : 'Revoked',
      };
  }
}

export const MICROSOFT_AUDIENCE_LABEL: Record<NonNullable<ProviderStatus['microsoft']>['audience'], string> = {
  single: 'One organisation only',
  organizations: 'Any work or school account',
  common: 'Any work, school or personal Microsoft account',
  consumers: 'Personal Microsoft accounts only',
};

/** Invite lifetimes; 7 days is only for viewer and operator invites. */
export const INVITE_HOURS: Array<{ value: string; label: string; maxRole: Role }> = [
  { value: '24', label: '24 hours', maxRole: 'owner' },
  { value: '72', label: '72 hours', maxRole: 'owner' },
  { value: '168', label: '7 days', maxRole: 'operator' },
];

export const SCOPE_PRESETS: Array<{ value: string; label: string; scopes: string[]; hint: string }> = [
  { value: 'all', label: 'Full role', scopes: [], hint: 'Everything the role allows.' },
  { value: 'read', label: 'Read only', scopes: ['read'], hint: 'State, audit, feed and logs. Cannot change anything.' },
  { value: 'act', label: 'Read and act', scopes: ['read', 'actions'], hint: 'Reads, and runs actions such as starting and stopping sessions.' },
  { value: 'start', label: 'Start sessions', scopes: ['read', 'session.start'], hint: 'Reads, and starts new sessions. Nothing else.' },
];

/** The Access option where a person picks capabilities by name. */
export const CUSTOM_ACCESS = { value: 'custom', label: 'Custom access', hint: 'Pick what it may do by name. Named capabilities work through the AI API and orgus mcp.' };

const ROLE_FOR_TIER: Record<CapabilityTier, Role> = { read: 'viewer', operate: 'operator', elevated: 'admin', critical: 'owner' };

export interface CapabilityChoice {
  cap: Capability;
  /** Why it cannot be given to this token, or null. */
  disabled: string | null;
}

/**
 * Custom access for a token of this kind and role: everyday changes (operate) apart from
 * elevated and critical ones. Nothing above the role is ever selectable; it stays listed,
 * disabled, with the role it needs.
 */
export function customChoices(role: Role, kind: ApiTokenInfo['kind']): { everyday: CapabilityChoice[]; elevated: CapabilityChoice[] } {
  const as = kind === 'agent' ? 'agent' : 'human';
  const top = tierRank(tokenCeiling(role, as));
  const choice = (cap: Capability): CapabilityChoice => ({
    cap,
    disabled:
      as === 'agent' && cap.tier === 'critical' ? 'Only a person may do this.' : tierRank(cap.tier) > top ? `Needs the ${ROLE_LABEL[ROLE_FOR_TIER[cap.tier]].toLowerCase()} role.` : null,
  });
  return {
    everyday: AI_CAPABILITIES.filter((c) => c.tier === 'operate').map(choice),
    elevated: AI_CAPABILITIES.filter((c) => c.tier === 'elevated' || c.tier === 'critical').map(choice),
  };
}

export function scopeSummary(t: Pick<ApiTokenInfo, 'scopes'>): string {
  if (!t.scopes.length) return 'Full role';
  const preset = SCOPE_PRESETS.find((p) => p.scopes.length === t.scopes.length && p.scopes.every((s) => t.scopes.includes(s)));
  if (preset) return preset.label;
  const named = t.scopes.map((s) => capabilityByName(s)).filter((c): c is Capability => !!c);
  if (!named.length || named.length + Number(t.scopes.includes('read')) !== t.scopes.length) return t.scopes.join(', ');
  const count = (n: number, one: string, many = `${one}s`) => `${n} ${n === 1 ? one : many}`;
  const high = (tier: CapabilityTier) => named.filter((c) => c.tier === tier).length;
  const parts = [t.scopes.includes('read') ? 'reads' : null, count(named.length, 'capability', 'capabilities')];
  const extra = [high('elevated') ? `${high('elevated')} elevated` : null, high('critical') ? `${high('critical')} critical` : null].filter(Boolean);
  return `Custom: ${parts.filter(Boolean).join(' and ')}${extra.length ? ` (${extra.join(', ')})` : ''}`;
}

export const TOKEN_DAYS = [
  { value: '1', label: '1 day' },
  { value: '7', label: '7 days' },
  { value: '14', label: '14 days' },
  { value: '30', label: '30 days' },
];

export { ROLE_LABEL };
